import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateManutenzionePdfBlob } from '../pdf/clientPdf';
import { salvaOffline, senzaRete, TIPI, MESSAGGIO_OFFLINE } from '../offline/salvaOffline';
import { Checkbox } from '../components/ui';
import DatiTrasferta, { CAMPI_TRASFERTA } from '../components/DatiTrasferta';

const API_BASE = '/api';  // work routes
const GAS_API_BASE = '/api';



const templates = [
  { id: 'CIESSE_IMPIANTI', label: 'Ciesse Impianti', file: '/Manutenzione/CiesseImpianti.pdf' },
  { id: 'CIESSE_SERVICE', label: 'Ciesse Service', file: '/Manutenzione/CiesseService.pdf' },
];

// 1–8 come Assegnate (8 con testo)
const CHECK_LABELS_1_8 = {
  1: 'Verifica 1: Struttura esterna: verifica visiva di eventuali danneggiamenti o rotture',
  2: 'Verifica 2: Guarnizione porta: verifica visiva del corretto montaggio e presenza di crepe o fessure',
  3: 'Verifica 3: Chiusura porta e interruttore: controllo efficienza di tenuta della guarnizione porta-verifica della chiusura e dell’allineamento',
  4: 'Verifica 4: Struttura interna e sistema di ventilazione: controllo visivo delle condizioni',
  5: 'Verifica 5: Cablaggi, connessioni elettriche e dispositivi interni: controllo visivo e manuale',
  6: 'Verifica 6: Teleruttori, Relè e Relè a stato solido: verifica visiva e stato dei contatti',
  7: 'Verifica 7: Controllo e pulizia condensatore: con aspiratore',
  8: 'Verifica 8: Verifica assorbimento elettrico',
};

const CHECK_LABELS_9_15 = {
  9: 'Verifica 9: Controllo valori conducibilità dell’acqua demineralizzata: misurazione della linea o del serbatoio di alimentazione conducibilità:',
  10: 'Verifica 10: Controllo dispositivi generatori di vapore: controllo caldaia, termostato, dispositivi di controllo',
  11: 'Verifica 11: Controllo dispositivi di sicurezza: simulazione intervento allarmi',
  12: 'Verifica 12: Controllo sistema di regolazione della Temperatura: Misurazione della temperatura interna in centro camera:',
  13: 'Verifica 13: Controllo sistema di regolazione Umidità relativa: Misurazione della UR% interna in centro camera:',
  14: 'Verifica 14: Controllo dispositivi di registrazione dati: controllo funzionamento eventuali dispositivi di acquisizione dati',
  15: 'Verifica 15: ALTRO',
};

function formatNow() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} alle ore ${hh}:${min}`;
}

function getCleanUserName(user) {
  if (!user) return '';
  const candidates = [];
  if (user.username) candidates.push(user.username);
  if (user.nome || user.cognome) candidates.push(`${user.nome || ''} ${user.cognome || ''}`.trim());
  if (user.fullName) candidates.push(user.fullName);

  let name = candidates.find(Boolean) || '';
  name = name
    .split(/\s+/)
    .filter((part) => !part.includes('@'))
    .join(' ')
    .trim();

  if (name) return name;

  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local.replace('.', ' ').trim();
  }
  return '';
}

function isValidItDate(s) {
  const str = String(s || '').trim();
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return false;
  const [dd, mm, yyyy] = str.split('/').map((x) => parseInt(x, 10));
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  if (yyyy < 2000 || yyyy > 2100) return false;
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
}

export default function Manutenzione() {
  const navigate = useNavigate();
  const location = useLocation();
const prefill = location.state?.prefill || null;

  const [selectedTemplate, setSelectedTemplate] = useState('CIESSE_IMPIANTI');
  const pdfUrl = useMemo(
    () => templates.find((t) => t.id === selectedTemplate)?.file || null,
    [selectedTemplate]
  );

  // WorkOrder al volo
  const [workOrderId, setWorkOrderId] = useState(null);
  const [internalCode, setInternalCode] = useState('');

  const [formData, setFormData] = useState({
    data: '',        // gg/mm/aaaa
    modello: '',
    inventario: '',
    matricola: '',
    allegatoAlVit: '',
    note: '',
    ...CAMPI_TRASFERTA,
  });

  // checks 1..15, 8 con note (come assegnate)
  const [checks, setChecks] = useState({
    1: { ok: false },
    2: { ok: false },
    3: { ok: false },
    4: { ok: false },
    5: { ok: false },
    6: { ok: false },
    7: { ok: false },
    8: { ok: false, note: '' }, // ✅ testo come Assegnate
    9: { ok: false, na: false, value: '' },
    10: { ok: false, na: false },
    11: { ok: false },
    12: { ok: false, tSet: '', tMis: '' },
    13: { ok: false, na: false, urSet: '', urMis: '' },
    14: { ok: false, na: false },
    15: { ok: false, note: '' },
  });

  // Firme
  const [tecnicoSignature, setTecnicoSignature] = useState(null);
  const [clienteSignature, setClienteSignature] = useState(location.state?.prefill?.clienteSignature || null);

  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // GAS
  const [gasUsedName, setGasUsedName] = useState('');
  const [gasUsedAmount, setGasUsedAmount] = useState('');
  const [gasRecoveredName, setGasRecoveredName] = useState('');
  const [gasRecoveredAmount, setGasRecoveredAmount] = useState('');
  const [availableGases, setAvailableGases] = useState([]);


  const [dateISO, setDateISO] = useState('');

  useEffect(() => {
    const st = location.state || {};
    if (st.workOrderId) setWorkOrderId(st.workOrderId);
    if (st.internalCode) setInternalCode(st.internalCode);
  
    const p = st.prefill || {};

    if (p?.clienteSignature) setClienteSignature(p.clienteSignature);

    if (p) {
      setFormData((prev) => ({
        ...prev,
        data: p.data ?? prev.data,
        allegatoAlVit: p.allegatoAlVit ?? prev.allegatoAlVit,
        note: p.note ?? prev.note,
        // se vuoi anche scrivere il luogo nelle note:
        // note: p.luogoIntervento ? `Luogo: ${p.luogoIntervento}\n${prev.note || ''}` : (p.note ?? prev.note),
      }));
    }
  }, [location.state]);
  
  const isoToIt = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const itToIso = (it) => {
    if (!it || !/^\d{2}\/\d{2}\/\d{4}$/.test(it)) return '';
    const [d, m, y] = it.split('/');
    return `${y}-${m}-${d}`;
  };

  // ============ GAS: load gases ============
  useEffect(() => {
    const loadMyGases = async () => {
      try {
        const meRes = await authFetch(`${GAS_API_BASE}/user`);
        if (!meRes.ok) return;
        const me = await meRes.json();
        const currentUserId = me.id || me._id;

        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (!gasRes.ok) return;
        const all = await gasRes.json();

        const myNames = Array.from(
          new Set(
            all
              .filter((g) => {
                const uid = g.user?._id || g.user?.id;
                return uid === currentUserId;
              })
              .map((g) => g.gasName)
          )
        ).sort();

        setAvailableGases(myNames);
        if (!gasUsedName && myNames.length === 1) setGasUsedName(myNames[0]);
      } catch (err) {
        console.error('Errore caricamento gas disponibili (MANUTENZIONE):', err);
      }
    };

    loadMyGases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // blocca scroll con modale firma
  useEffect(() => {
    if (signModal.open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [signModal.open]);

  // ============ HANDLERS ============
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleCheck = (num, field) => {
    setChecks((prev) => {
      const current = prev[num] || {};
      const nextValue = !current[field];

      const next = {
        ...prev,
        [num]: {
          ...current,
          [field]: nextValue,
        },
      };

      // Mutua esclusione OK <-> NA
      if (field === 'ok' && nextValue === true && 'na' in current) {
        next[num].na = false;
      }
      if (field === 'na' && nextValue === true) {
        next[num].ok = false;
        if ('value' in current) next[num].value = '';
        if ('tSet' in current) next[num].tSet = '';
        if ('tMis' in current) next[num].tMis = '';
        if ('urSet' in current) next[num].urSet = '';
        if ('urMis' in current) next[num].urMis = '';
        if ('note' in current) next[num].note = '';
      }

      // ✅ come Assegnate: se togli OK alla 8 pulisco nota
      if (num === 8 && field === 'ok' && nextValue === false) {
        next[num].note = '';
      }

      // se togli OK alla 15 pulisco note
      if (num === 15 && field === 'ok' && nextValue === false) {
        next[num].note = '';
      }

      return next;
    });
  };

  const handleCheckValueChange = (num, field, value) => {
    setChecks((prev) => ({
      ...prev,
      [num]: {
        ...prev[num],
        [field]: value,
      },
    }));
  };

  // ============ FIRMA: FIX PUNTATORE (Pointer Events + scaling) ============
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;

    // pulizia
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getCanvasPoint = (evt) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = evt.clientX ?? (evt.touches ? evt.touches[0].clientX : 0);
    const clientY = evt.clientY ?? (evt.touches ? evt.touches[0].clientY : 0);

    // ✅ scaling corretto tra CSS size e canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    canvas.setPointerCapture?.(e.pointerId);

    isDrawingRef.current = true;
    const p = getCanvasPoint(e);
    lastPointRef.current = p;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const onPointerMove = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getCanvasPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const onPointerUp = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    isDrawingRef.current = false;
    canvas.releasePointerCapture?.(e.pointerId);
  };

  const openSignatureModal = (type) => {
    setSignModal({ open: true, type });
    setTimeout(() => setupCanvas(), 0);
  };

  const clearSignatureCanvas = () => {
    setupCanvas();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    if (signModal.type === 'tecnico') setTecnicoSignature(dataUrl);
    if (signModal.type === 'cliente') setClienteSignature(dataUrl);
    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') setTecnicoSignature(null);
    if (type === 'cliente') setClienteSignature(null);
  };

  // ============ GAS: consume + notify ============
  const consumeGasAndNotify = async () => {
    try {
      const meRes = await authFetch(`${GAS_API_BASE}/user`);
      if (!meRes.ok) return;
      const me = await meRes.json();
      const currentUserId = me.id || me._id;

      const messageParts = [];

      const usedAmountNum = Math.round(parseFloat(String(gasUsedAmount || '').replace(',', '.')));
      if (gasUsedName && !Number.isNaN(usedAmountNum) && usedAmountNum > 0) {
        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (gasRes.ok) {
          const all = await gasRes.json();
          const target = all.find((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId && g.gasName === gasUsedName;
          });

          if (target) {
            const currentQty = Number(target.quantityGrams || 0);
            const newQty = Math.max(0, currentQty - usedAmountNum);

            await authFetch(`${GAS_API_BASE}/gas/${target._id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            });

            messageParts.push(`ha utilizzato ${usedAmountNum} g di ${gasUsedName}`);
          }
        }
      }

      const recoveredAmountNum = Math.round(parseFloat(String(gasRecoveredAmount || '').replace(',', '.')));
      if (gasRecoveredName && !Number.isNaN(recoveredAmountNum) && recoveredAmountNum > 0) {
        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (gasRes.ok) {
          const all = await gasRes.json();
          const target = all.find((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId && g.gasName === gasRecoveredName;
          });

          if (target) {
            const currentQty = Number(target.quantityGrams || 0);
            const newQty = currentQty + recoveredAmountNum;

            await authFetch(`${GAS_API_BASE}/gas/${target._id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            });

            messageParts.push(`ha recuperato ${recoveredAmountNum} g di ${gasRecoveredName}`);
          } else {
            await authFetch(`${GAS_API_BASE}/gas`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                gasName: gasRecoveredName,
                quantityGrams: recoveredAmountNum,
              }),
            });

            messageParts.push(`ha recuperato ${recoveredAmountNum} g di ${gasRecoveredName}`);
          }
        }
      }

      if (messageParts.length > 0) {
        const displayName = getCleanUserName(me);
        const when = formatNow();
        const message = displayName
          ? `${displayName} ${messageParts.join(' e ')} durante una manutenzione il ${when}.`
          : `${messageParts.join(' e ')} durante una manutenzione il ${when}.`;

        await authFetch(`${GAS_API_BASE}/notifications`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'gas_usage',
            message,
            roles: ['admin', 'gestione', 'segreteria'],
          }),
        });
      }
    } catch (err) {
      console.error('Errore consumo/recupero gas (MANUTENZIONE):', err);
    }
  };

  // ============ CREA WORKORDER PER CODICE INTERNO ============
  const ensureWorkOrder = async () => {
    if (workOrderId && internalCode) return { workOrderId, internalCode };

    const res = await authFetch(`${API_BASE}/work/maintenance/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: formData.data,
        modello: formData.modello,
        inventario: formData.inventario,
        matricola: formData.matricola,
        allegatoAlVit: formData.allegatoAlVit,
        note: formData.note,
      }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || 'Errore creazione manutenzione (init).');

    const wo = data?.workOrder;
    setWorkOrderId(wo?._id || null);
    setInternalCode(wo?.internalCode || '');

    return { workOrderId: wo?._id, internalCode: wo?.internalCode || '' };
  };

  // ============ GENERA PDF + COMPLETE ============
  const handleGeneratePdf = async () => {
    setError('');
    setSuccessMessage('');


    if (!formData.data || !isValidItDate(formData.data)) {
      setError('Inserisci una data valida in formato gg/mm/aaaa.');
      return;
    }
    if (!String(formData.modello || '').trim()) {
      setError('Compila il campo Modello prima di generare il PDF.');
      return;
    }
    if (!String(formData.inventario || '').trim()) {
      setError('Compila il campo Inventario prima di generare il PDF.');
      return;
    }
    if (!String(formData.matricola || '').trim()) {
      setError('Compila il campo Matricola prima di generare il PDF.');
      return;
    }

    setLoading(true);

    // SENZA RETE: il rapportino resta sul dispositivo e parte da solo.
    if (senzaRete()) {
      try {
        await salvaOffline({
          tipo: TIPI.MANUTENZIONE,
          template: selectedTemplate,
          formData,
          checks,
          tecnicoSignature,
          clienteSignature,
          datiIniziali: {
            data: formData.data,
            modello: formData.modello,
            inventario: formData.inventario,
            matricola: formData.matricola,
            allegatoAlVit: formData.allegatoAlVit,
            note: formData.note,
          },
          summary: {
            data: formData.data,
            modello: formData.modello,
            inventario: formData.inventario,
            matricola: formData.matricola,
            note: formData.note,
          },
          gas: { gasUsedName, gasUsedAmount, gasRecoveredName, gasRecoveredAmount },
        });

        setSuccessMessage(MESSAGGIO_OFFLINE);
      } catch (err) {
        console.error('Errore salvataggio offline:', err);
        setError(err.message || 'Non sono riuscito a salvare il rapportino sul dispositivo.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const { workOrderId: woId, internalCode: code } = await ensureWorkOrder();

      // PDF
      const pdfBody = {
        template: selectedTemplate,
        formData: { ...formData, codiceInterno: code },
        checks,
        tecnicoSignature,
        clienteSignature,
      };

      const blob = await generateManutenzionePdfBlob({ ...pdfBody, inviaReport: true });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verbale_manutenzione_${code || 'MXX-00000'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // GAS
      await consumeGasAndNotify();

      // COMPLETE (salva in terminate + mail/notifiche dal backend)
      const saveRes = await authFetch(`${API_BASE}/work/maintenance/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workOrderId: woId,
          formData: { ...formData, codiceInterno: code },
          checks,
          tecnicoSignature,
          clienteSignature,
          template: selectedTemplate,
          summary: {
            codiceInterno: code,
            data: formData.data,
            modello: formData.modello,
            inventario: formData.inventario,
            matricola: formData.matricola,
            note: formData.note,
          },
        }),
      });

      const saveData = await saveRes.json().catch(() => null);
      if (!saveRes.ok) throw new Error(saveData?.error || 'Errore salvataggio manutenzione.');

      setSuccessMessage(`PDF generato e manutenzione salvata. Codice interno: ${code}`);
      // navigate('/lavorazioni/terminate');
      navigate('/manutenzioni-multiple', {
        state: { completedWorkOrderId: woId },
      });
    } catch (err) {
      console.error('Errore generazione/salvataggio manutenzione:', err);
      setError(err.message || 'Errore inatteso');
    } finally {
      setLoading(false);
    }
  };

  const canGeneratePdf = !!tecnicoSignature && !!clienteSignature && !loading;

  // ============ UI ============
  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
              Verbale di manutenzione
            </h1>
            <p className="text-sm text-gray-600">
              Compila i dati, checklist, firme e genera il PDF: la manutenzione finisce direttamente nelle lavorazioni terminate.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        {/* TEMPLATE (non è una box “operativa”, sta sopra) */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/Manutenzione/', '')}
              </p>
            </button>
          ))}
        </div>

        {/* MESSAGGI */}
        {(error || successMessage) && (
          <div className="mb-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                {successMessage}
              </div>
            )}
          </div>
        )}

        {/* ============================
            BOX 1) GAS (opzionale)
        ============================ */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-2">Gas (opzionale)</h3>
          <p className="text-sm text-gray-500">
            Questi dati non vanno nel PDF. Servono per aggiornare i livelli gas e inviare la notifica interna.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gas utilizzato</label>
              <select
                value={gasUsedName}
                onChange={(e) => setGasUsedName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              >
                <option value="">Nessun gas utilizzato</option>
                {availableGases.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Quantità usata (g)</label>
              <input
                value={gasUsedAmount}
                onChange={(e) => setGasUsedAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Es. 250"
                inputMode="numeric"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gas recuperato</label>
              <select
                value={gasRecoveredName}
                onChange={(e) => setGasRecoveredName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              >
                <option value="">Nessun gas recuperato</option>
                {availableGases.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Quantità recuperata (g)</label>
              <input
                value={gasRecoveredAmount}
                onChange={(e) => setGasRecoveredAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Es. 150"
                inputMode="numeric"
              />
            </div>
          </div>
        </section>

        {/* ============================
            BOX 2) DATI MANUTENZIONE
        ============================ */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Dati manutenzione</h3>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Codice interno (generato automaticamente al click)
            </label>
            <input
              value={internalCode || ''}
              disabled
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
              placeholder="(sarà generato automaticamente)"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data (gg/mm/aaaa) *</label>
              <input
                type="date"
                value={dateISO}
                onChange={(e) => {
                  const iso = e.target.value;
                  setDateISO(iso);
                  setFormData((p) => ({ ...p, data: isoToIt(iso) })); // ✅ salva in IT
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Inventario *</label>
              <input
                name="inventario"
                value={formData.inventario}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Inventario"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Modello *</label>
              <input
                name="modello"
                value={formData.modello}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Modello"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Matricola *</label>
              <input
                name="matricola"
                value={formData.matricola}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Matricola"
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Allegato al VIT (opzionale)
            </label>
            <input
              name="allegatoAlVit"
              value={formData.allegatoAlVit}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Rif. VIT / note interne"
            />
          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Note intervento / osservazioni..."
            />
          </div>
        </section>

        <DatiTrasferta formData={formData} onChange={handleChange} className="mb-6" />

        {/* ============================
            BOX 3) CHECKLIST VERIFICHE
        ============================ */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-2">Checklist verifiche</h3>
          <p className="text-xs text-gray-500 mb-4">
            Spunta le verifiche eseguite. Alcune hanno N/A o valori da indicare.
          </p>

          <div className="space-y-3">
            {/* 1..8 */}
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="space-y-2 border rounded-xl p-3 bg-white">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[n].ok}
                    onChange={() => toggleCheck(n, 'ok')}
                  />
                  <span>{CHECK_LABELS_1_8[n]}</span>
                </label>

                {/* ✅ testo aggiuntivo per verifica 8 */}
                {n === 8 && (
                  <input
                    type="text"
                    value={checks[8].note || ''}
                    onChange={(e) => handleCheckValueChange(8, 'note', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="Valore / nota verifica 8 (facoltativo)"
                    disabled={!checks[8].ok}
                  />
                )}
              </div>
            ))}

            {/* 9 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[9].ok}
                    onChange={() => toggleCheck(9, 'ok')}
                    className="mt-1"
                  />
                  <span>{CHECK_LABELS_9_15[9]}</span>
                </label>

                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[9].na}
                    onChange={() => toggleCheck(9, 'na')}
                  />
                  N/A
                </label>
              </div>

              <input
                type="text"
                value={checks[9].value || ''}
                disabled={!!checks[9].na}
                onChange={(e) => handleCheckValueChange(9, 'value', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                placeholder="Valore rilevato"
              />
            </div>

            {/* 10 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[10].ok}
                    onChange={() => toggleCheck(10, 'ok')}
                    className="mt-1"
                  />
                  <span>{CHECK_LABELS_9_15[10]}</span>
                </label>

                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[10].na}
                    onChange={() => toggleCheck(10, 'na')}
                  />
                  N/A
                </label>
              </div>
            </div>

            {/* 11 */}
            <div className="border rounded-xl p-3 bg-white">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={!!checks[11].ok}
                  onChange={() => toggleCheck(11, 'ok')}
                  className="mt-1"
                />
                <span>{CHECK_LABELS_9_15[11]}</span>
              </label>
            </div>

            {/* 12 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[12].ok}
                    onChange={() => toggleCheck(12, 'ok')}
                    className="mt-1"
                  />
                  <span>{CHECK_LABELS_9_15[12]}</span>
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={checks[12].tSet || ''}
                  onChange={(e) => handleCheckValueChange(12, 'tSet', e.target.value)}
                  disabled={false}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  placeholder="T set"
                />
                <input
                  type="text"
                  value={checks[12].tMis || ''}
                  onChange={(e) => handleCheckValueChange(12, 'tMis', e.target.value)}
                  disabled={false}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  placeholder="T mis"
                />
              </div>
            </div>

            {/* 13 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[13].ok}
                    onChange={() => toggleCheck(13, 'ok')}
                    className="mt-1"
                  />
                  <span>{CHECK_LABELS_9_15[13]}</span>
                </label>

                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[13].na}
                    onChange={() => toggleCheck(13, 'na')}
                  />
                  N/A
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={checks[13].urSet || ''}
                  onChange={(e) => handleCheckValueChange(13, 'urSet', e.target.value)}
                  disabled={!!checks[13].na}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  placeholder="UR set"
                />
                <input
                  type="text"
                  value={checks[13].urMis || ''}
                  onChange={(e) => handleCheckValueChange(13, 'urMis', e.target.value)}
                  disabled={!!checks[13].na}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  placeholder="UR mis"
                />
              </div>
            </div>

            {/* 14 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[14].ok}
                    onChange={() => toggleCheck(14, 'ok')}
                    className="mt-1"
                  />
                  <span>{CHECK_LABELS_9_15[14]}</span>
                </label>

                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[14].na}
                    onChange={() => toggleCheck(14, 'na')}
                  />
                  N/A
                </label>
              </div>
            </div>

            {/* 15 */}
            <div className="border rounded-xl p-3 bg-white space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={!!checks[15].ok}
                  onChange={() => toggleCheck(15, 'ok')}
                  className="mt-1"
                />
                <span>{CHECK_LABELS_9_15[15]}</span>
              </label>

              <textarea
                value={checks[15].note || ''}
                onChange={(e) => handleCheckValueChange(15, 'note', e.target.value)}
                rows={2}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full"
                placeholder="Note aggiuntive"
              />
            </div>
          </div>
        </section>

        {/* ============================
            BOX 4) FIRME
        ============================ */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h3 className="font-semibold text-gray-800">Firme</h3>
              <p className="text-xs text-gray-500">
                Firma con mouse o dito. (Salvate digitalmente)
              </p>
            </div>

            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm ${
                loading ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
              title="Genera PDF e salva la manutenzione"
            >
              {loading ? 'Generazione...' : 'Genera PDF'}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {/* tecnico */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Firma tecnico</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openSignatureModal('tecnico')}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {tecnicoSignature ? 'Modifica' : 'Aggiungi'}
                  </button>
                  {tecnicoSignature && (
                    <button
                      type="button"
                      onClick={() => clearSignaturePreview('tecnico')}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancella
                    </button>
                  )}
                </div>
              </div>

              <div className="border rounded-lg p-3 min-h-[120px] flex items-center justify-center bg-white">
                {tecnicoSignature ? (
                  <img
                    src={tecnicoSignature}
                    alt="Firma tecnico"
                    className="max-h-[140px] object-contain"
                  />
                ) : (
                  <span className="text-sm text-gray-400">Nessuna firma</span>
                )}
              </div>
            </div>

            {/* cliente */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Firma cliente</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openSignatureModal('cliente')}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {clienteSignature ? 'Modifica' : 'Aggiungi'}
                  </button>
                  {clienteSignature && (
                    <button
                      type="button"
                      onClick={() => clearSignaturePreview('cliente')}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancella
                    </button>
                  )}
                </div>
              </div>

              <div className="border rounded-lg bg-gray-50 h-28 flex items-center justify-center overflow-hidden">
                {clienteSignature ? (
                  <img src={clienteSignature} alt="Firma cliente" className="h-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">Nessuna firma</span>
                )}
              </div>
            </div>
          </div>

          {(error || successMessage) && (
            <div className="mt-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                  {successMessage}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ============================
            BOX 5) ANTEPRIMA
        ============================ */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Anteprima modello</h3>
          {pdfUrl ? (
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <iframe title="preview" src={pdfUrl} className="w-full h-[600px]" />
            </div>
          ) : (
            <p className="text-sm text-gray-500">Seleziona un template per visualizzare l’anteprima.</p>
          )}
        </section>
      </div>

      {/* MODALE FIRMA (FIX puntatore) */}
      {signModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onPointerMove={(e) => e.preventDefault()}
        >
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">
                Firma {signModal.type === 'tecnico' ? 'tecnico' : 'cliente'}
              </h3>
              <button
                type="button"
                onClick={() => setSignModal({ open: false, type: null })}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-2">
              Firma con mouse o dito. Il puntatore ora è calibrato correttamente.
            </p>

            <div className="border border-gray-300 rounded-lg bg-gray-50 overflow-hidden">
              <canvas
                ref={canvasRef}
                width={900}
                height={360}
                className="w-full h-[220px]"
                style={{ touchAction: 'none' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
              />
            </div>

            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={clearSignatureCanvas}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Cancella
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSignModal({ open: false, type: null })}
                  className="px-3 py-2 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                >
                  Salva firma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
